import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "pr_access_token";
const AuthContext = createContext(null);

// Attach stored token on every request. Robust across cookie-blocking browsers
// (Safari ITP, 3rd-party cookie blockers, incognito, cross-site deploys).
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auto-logout on 401 + NORMALIZE validation errors so toast.error(e.response.data.detail)
// can never render an Object or Array and crash React (the "white screen" bug).
// FastAPI/Pydantic returns detail as an ARRAY of {loc, msg, type} objects on 422;
// we flatten it to a human-readable string before the caller sees it.
function _stringifyDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    try {
      return detail
        .map((d) => {
          if (!d) return "";
          if (typeof d === "string") return d;
          const loc = Array.isArray(d.loc) ? d.loc.filter((x) => x !== "body").join(".") : "";
          const msg = d.msg || d.message || "Invalid value";
          return loc ? `${loc}: ${msg}` : msg;
        })
        .filter(Boolean)
        .join(" · ");
    } catch {
      return "Request failed";
    }
  }
  if (typeof detail === "object") {
    try { return JSON.stringify(detail); } catch { return "Request failed"; }
  }
  return String(detail);
}

axios.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401 && !err.config?.url?.includes("/auth/")) {
      localStorage.removeItem(TOKEN_KEY);
    }
    // Normalize detail to a string so downstream toast.error/JSX renders don't crash
    if (err.response?.data?.detail != null && typeof err.response.data.detail !== "string") {
      err.response.data.detail = _stringifyDetail(err.response.data.detail);
    }
    return Promise.reject(err);
  }
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch {
      setUser(false);
      localStorage.removeItem(TOKEN_KEY);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(
      `${API}/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    if (data.access_token) {
      localStorage.setItem(TOKEN_KEY, data.access_token);
    }
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch {}
    localStorage.removeItem(TOKEN_KEY);
    setUser(false);
  };

  const impersonate = async (userId) => {
    const { data } = await axios.post(
      `${API}/admin/impersonate/${userId}`,
      {},
      { withCredentials: true }
    );
    if (data.access_token) {
      localStorage.setItem(TOKEN_KEY, data.access_token);
    }
    // Force full refresh so all app state reloads under the impersonated identity
    window.location.href = "/admin";
  };

  const stopImpersonating = async () => {
    const { data } = await axios.post(
      `${API}/admin/impersonate/stop`,
      {},
      { withCredentials: true }
    );
    if (data.access_token) {
      localStorage.setItem(TOKEN_KEY, data.access_token);
    }
    window.location.href = "/admin";
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, impersonate, stopImpersonating }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
